import React, { RefObject } from "react";
import { Platform } from "../../../../types/Platforms";
import { ChatStreamError } from "../../../../services/chatService";
import { UIAction } from "../types";
import { NewChatWelcomeSuggestions } from "./NewChatWelcomeSuggestions";
import { ConversationStarters } from "./ConversationStarters";
import { ChatInputArea } from "./ChatInputArea";
import { ChatErrorBanner } from "./ChatErrorBanner";
import { SaveToPlatformPanel, VideoForPlatform } from "../../../../features/video-management";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";

/** A starter card that opens a conversation with the agent's own first reply. */
export type AgentProposalStarter = { cardLabel: string; cardIcon: string; onStart: () => void };

type NewChatWelcomeViewProps = {
  surfaceBg: string;
  messagesContainerRef: RefObject<HTMLDivElement>;
  starterActionsForDisplay: UIAction[];
  isLoading: boolean;
  onActionClick: (action: UIAction) => void;
  starterSuggestionAccentPill: string;
  starterSuggestionNeutralPill: string;
  showConversationStarters: boolean;
  conversationStarters: Array<{ id: string; suggestion: string }>;
  onConversationStarter: (suggestion: string) => void;
  error: ChatStreamError | string | null;
  onDismissError: () => void;
  chatInputSharedProps: any;
  showSaveToPlatform: boolean;
  videoForPlatform: VideoForPlatform | null;
  onCloseSavePanel: () => void;
  customerId?: string;
  idToken: string;
  agentId?: string;
  saveToPlatformInitialPlatforms: Platform[];
  isExiting?: boolean;
  userName?: string;
  /** SMS/email agents: avoid "create" copy; default is content/UGC welcome. */
  welcomeVariant?: "default" | "communication";
  /** When set, replaces starter actions with the streaming chip suggestions card. */
  chipSuggestionsCard?: React.ReactNode;
  /** An agent-specific starter card (Visibility Manager: Google Business Profile fixes). */
  agentProposal?: AgentProposalStarter;
};

export const NewChatWelcomeView: React.FC<NewChatWelcomeViewProps> = ({
  surfaceBg,
  messagesContainerRef,
  starterActionsForDisplay,
  isLoading,
  onActionClick,
  starterSuggestionAccentPill,
  starterSuggestionNeutralPill,
  showConversationStarters,
  conversationStarters,
  onConversationStarter,
  error,
  onDismissError,
  chatInputSharedProps,
  showSaveToPlatform,
  videoForPlatform,
  onCloseSavePanel,
  customerId,
  idToken,
  agentId,
  saveToPlatformInitialPlatforms,
  isExiting = false,
  userName,
  welcomeVariant = "default",
  chipSuggestionsCard,
  agentProposal,
}) => {
  const firstName = (userName || "").trim().split(/\s+/)[0] || "there";
  const welcomeHeadline =
    welcomeVariant === "communication"
      ? `Hi ${firstName}, ask me about your calls, inbox, or calendar — or type anything.`
      : `Hi ${firstName}, what would you like to create today?`;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        ref={messagesContainerRef}
        className={`min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto ${surfaceBg}`}
      >
        <div
          className={`flex min-h-full flex-col justify-start space-y-8 px-4 pb-10 pt-10 transition-all duration-300 sm:px-6 sm:pt-12 ${CHAT_COLUMN_CLASS} ${
            isExiting ? "translate-y-6 opacity-0" : "translate-y-0 opacity-100"
          }`}
        >
          <div className="text-center">
            <h3 className="text-[1.125rem] font-semibold text-theme-fg">
              {welcomeHeadline}
            </h3>
          </div>

          {chipSuggestionsCard ?? (
            <>
              <NewChatWelcomeSuggestions
                actions={starterActionsForDisplay}
                isLoading={isLoading}
                onActionClick={onActionClick}
                accentPillClass={starterSuggestionAccentPill}
                neutralPillClass={starterSuggestionNeutralPill}
                mode="all"
                extraCard={
                  agentProposal
                    ? {
                        label: agentProposal.cardLabel,
                        icon: agentProposal.cardIcon,
                        onClick: agentProposal.onStart,
                      }
                    : undefined
                }
              />
              {showConversationStarters && (
                <ConversationStarters
                  hideHero
                  starters={conversationStarters}
                  onSelect={onConversationStarter}
                  isLoading={isLoading}
                />
              )}
            </>
          )}

          {error && (
            <div className="flex w-full justify-center">
              <ChatErrorBanner error={error} onDismiss={onDismissError} />
            </div>
          )}

          <div className="w-full min-w-0 pt-8">
            <ChatInputArea {...chatInputSharedProps} variant="inline" />
          </div>
        </div>
      </div>

      {showSaveToPlatform && videoForPlatform && (
        <SaveToPlatformPanel
          isOpen={showSaveToPlatform}
          onClose={onCloseSavePanel}
          video={videoForPlatform}
          customerId={customerId || ""}
          idToken={idToken}
          agentId={agentId || ""}
          initialSelectedPlatforms={saveToPlatformInitialPlatforms}
        />
      )}
    </div>
  );
};
